import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Plus, Trash2, IndianRupee, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';

const SERVICE_OPTIONS = [
  'Elder Care',
  'Baby Care',
  'Injection',
  'IV Drip',
  'Dressing',
  'Catheter Care',
  'Post Surgery Care',
  'Bedridden Patient Care',
  'BP / Sugar Check',
  'ICU Trained Nursing',
  'Home Attendant',
  'Night Duty Care',
];

const PRICING_OPTIONS = [
  'Per Visit',
  'Per Hour',
  'Per 8 Hour Shift',
  'Per 12 Hour Shift',
  'Per Day',
  'Per Night',
  'Per Week',
  'Per Month',
];

const EMPTY_SERVICE = { name: '', price: '', pricing_type: '' };

export const NurseServicesForm = ({ nurseServices, setNurseServices }) => {
  const addService = () => {
    setNurseServices([...nurseServices, { ...EMPTY_SERVICE }]);
  };

  const removeService = (index) => {
    if (nurseServices.length <= 1) {
      toast.error('At least one service is required');
      return;
    }
    setNurseServices(nurseServices.filter((_, i) => i !== index));
  };

  const updateService = (index, field, value) => {
    if (field === 'name') {
      const isDuplicate = nurseServices.some((s, i) => i !== index && s.name === value);
      if (isDuplicate) {
        toast.error(`"${value}" already added`);
        return;
      }
    }
    const updated = nurseServices.map((s, i) => i === index ? { ...s, [field]: value } : s);
    setNurseServices(updated);
  };

  const usedNames = nurseServices.map(s => s.name).filter(Boolean);
  const completedServices = nurseServices.filter(s => s.name && s.price && s.pricing_type);

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-[#0F62FE]">
          <Stethoscope className="w-5 h-5" />
          Services & Pricing
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">Add the nursing services you offer with pricing</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {nurseServices.map((service, index) => (
          <div
            key={index}
            className="grid grid-cols-1 sm:grid-cols-[1fr_120px_1fr_40px] gap-3 items-end p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-colors"
            data-testid={`nurse-service-row-${index}`}
          >
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Service Name *</Label>
              <Select value={service.name} onValueChange={v => updateService(index, 'name', v)}>
                <SelectTrigger data-testid={`service-name-${index}`}>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt} disabled={usedNames.includes(opt) && service.name !== opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Price (₹) *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  value={service.price}
                  onChange={e => updateService(index, 'price', e.target.value)}
                  placeholder="0"
                  className="pl-7"
                  data-testid={`service-price-${index}`}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Pricing Type *</Label>
              <Select value={service.pricing_type} onValueChange={v => updateService(index, 'pricing_type', v)}>
                <SelectTrigger data-testid={`service-pricing-${index}`}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {PRICING_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-red-400 hover:text-red-600 hover:bg-red-50 h-10 w-10 self-end"
              onClick={() => removeService(index)}
              data-testid={`remove-service-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          className="w-full border-dashed border-2 border-slate-300 hover:border-[#0F62FE] hover:text-[#0F62FE] h-11"
          onClick={addService}
          data-testid="add-service-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Service
        </Button>

        {/* Live Preview */}
        {completedServices.length > 0 && (
          <div className="mt-4 p-4 bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-xl border border-slate-200" data-testid="services-preview">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <IndianRupee className="w-4 h-4 text-[#0F62FE]" />
              Services Preview
            </h4>
            <div className="space-y-2">
              {completedServices.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-slate-100">
                  <span className="text-sm font-medium text-slate-800">{s.name}</span>
                  <span className="text-sm text-[#0F62FE] font-semibold">₹{s.price} / {s.pricing_type}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NurseServicesForm;
